import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { JsonLdScript } from 'components/atoms';
import { formatTimestamp } from 'utils/dateUtils';

const DAY_MAP = {
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
	sunday: 'Sunday',
};

const buildAmenityFeatures = (storageUnits) => {
	if (!storageUnits?.length) return [];

	const seen = new Set();
	const features = [];
	storageUnits.forEach((unit) => {
		(unit.availableAmenities || []).forEach((amenity) => {
			if (!seen.has(amenity)) {
				seen.add(amenity);
				features.push({
					'@type': 'LocationFeatureSpecification',
					name: amenity,
					value: 'true',
				});
			}
		});
	});
	return features;
};

const buildOpeningHoursSpecification = (hours) => {
	if (!hours?.office) return [];

	const grouped = {};
	Object.entries(hours.office).forEach(([day, hour]) => {
		if (
			hour?.open &&
			hour?.closed &&
			typeof hour.open === 'string' &&
			typeof hour.closed === 'string' &&
			hour.open !== '-' &&
			hour.closed !== '-'
		) {
			const mappedDay = DAY_MAP[day];
			if (!mappedDay) return;
			const key = `${hour.open.slice(0, 5)}_${hour.closed.slice(0, 5)}`;
			if (!grouped[key]) {
				grouped[key] = {
					opens: hour.open.slice(0, 5),
					closes: hour.closed.slice(0, 5),
					days: [],
				};
			}
			grouped[key].days.push(mappedDay);
		}
	});

	return Object.values(grouped).map(({ opens, closes, days }) => ({
		'@type': 'OpeningHoursSpecification',
		dayOfWeek: days,
		opens,
		closes,
	}));
};

const buildImages = (images) => {
	if (!images?.length) return [];
	return images.map((img) => ({
		'@type': 'ImageObject',
		url: `https://images.selfstorage.com/small-compress/${img}`,
	}));
};

const buildPaymentAccepted = (featuresDataByCategory) => {
	const items = featuresDataByCategory?.payments?.items;
	if (!items?.length) return undefined;
	const accepted = items
		.filter((item) => item.hasAttribute === true)
		.map((item) => item.label);
	return accepted.length ? accepted : undefined;
};

const buildReviewSchema = (googleReviews) => {
	const schemaReviews = [];

	const googleReviewList = googleReviews?.reviews;
	if (Array.isArray(googleReviewList)) {
		googleReviewList.forEach((review) => {
			if (!review) return;
			schemaReviews.push({
				'@type': 'Review',
				author: {
					'@type': 'Person',
					name: review.author_name || 'Anonymous',
				},
				...(formatTimestamp(review.time, 'iso') && {
					datePublished: formatTimestamp(review.time, 'iso'),
				}),
				reviewBody: review.text || '',
				reviewRating: {
					'@type': 'Rating',
					ratingValue: String(review.rating ?? ''),
					bestRating: '5',
					worstRating: '1',
				},
			});
		});
	}

	return schemaReviews;
};

const PRODUCTION_BASE_URL = 'https://www.selfstorage.com';

export function SelfStorageSchema({
	facility,
	phone,
	baseUrl,
	units,
	googleReviews,
}) {
	const rootUrl = baseUrl || PRODUCTION_BASE_URL;
	const paymentAccepted = buildPaymentAccepted(
		facility?.featuresDataByCategory,
	);
	const reviewSchema = buildReviewSchema(googleReviews);

	const street1 = facility?.location?.streetAddress1 || '';
	const street2Suffix = facility?.location?.streetAddress2
		? `, ${facility.location.streetAddress2}`
		: '';
	const streetAddress = `${street1}${street2Suffix}`;

	const selfStorageSchema = {
		'@context': 'https://schema.org',
		'@type': 'SelfStorage',
		name: facility?.name,
		url: facility?.url ? `${rootUrl}${facility.url}` : rootUrl,
		address: {
			'@type': 'PostalAddress',
			streetAddress,
			addressLocality: facility?.location?.city,
			addressRegion: facility?.location?.state,
			postalCode: facility?.location?.zip,
			addressCountry: 'US',
		},
		geo: {
			'@type': 'GeoCoordinates',
			latitude: facility?.location?.latitude,
			longitude: facility?.location?.longitude,
		},
		telephone: get(phone, 'formattedNumber'),
		...(get(facility, 'reviews.average') &&
			get(facility, 'reviews.number') && {
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: facility.reviews.average,
					ratingCount: facility.reviews.number,
				},
			}),
		...(reviewSchema.length > 0 && { review: reviewSchema }),
		priceRange: '$$',
		...(paymentAccepted && {
			paymentAccepted,
		}),
		image: buildImages(facility?.images),
		amenityFeature: buildAmenityFeatures(units?.storageUnits),
		openingHoursSpecification: buildOpeningHoursSpecification(
			facility?.hours,
		),
		...(facility?.location?.latitude &&
			facility?.location?.longitude && {
				hasMap: `https://www.google.com/maps?q=${facility.location.latitude},${facility.location.longitude}`,
			}),
	};

	return <JsonLdScript schema={selfStorageSchema} />;
}

SelfStorageSchema.propTypes = {
	facility: PropTypes.object,
	phone: PropTypes.object,
	baseUrl: PropTypes.string,
	units: PropTypes.object,
	googleReviews: PropTypes.object,
};

export default SelfStorageSchema;
